import classNames from "classnames"
import get from "lodash/get"
import React from "react"
import { imageItemTransformer } from "../utils/transformers"
import BodyStyles from "./BodyStyles"
import Image from "./ImageOptim"
import RichText from "./RichText"
import Heading from "./Heading"
import LinkElement from "./LinkElement"

interface MediaProps {
  /**
   * The content item to display as the media in the tile
   */
  contentItem: any
  /**
   * The widest width the media will be displayed at. Used to optimize the image
   */
  cropWidth: number
  /**
   * The ratio to crop and display media in the tile. Used to set the minimum
   * height of the when the text column is shorter than the media column
   */
  mediaRatio?: 0.666 | 1 | 1.5
}

const Media = ({ contentItem, cropWidth, mediaRatio }: MediaProps) => {
  const type = get(contentItem, "system.type")
  if (type === "image_element") {
    return (
      <>
        <div className={`absolute inset-0`}>
          <Image
            {...imageItemTransformer(contentItem)}
            cropWidth={cropWidth}
            cropRatio={mediaRatio || 1}
            cover={true}
            className="media-tile__media"
            suppressCaption={true}
          />
        </div>
        {/* This `canvas` element is used to set a min-size so tiles w/ 
            short text don’t have squatty photos
         */}
        <canvas
          className={`w-full max-w-full h-auto block`}
          width="375"
          height={375 * (mediaRatio || 1)}
        />
      </>
    )
  }

  return null
}

export interface MediaTileProps {
  /**
   * The variant of the tile
   *
   * - `default` - displays the tile without a border or shadow so it doesn’t
   * stand apart from other content on the page
   * - `card` - displays the tile as a card so it stands apart from other
   * content on the page
   */
  style?: "card" | "default"
  /**
   * The maximum width the tile will be displayed at. This is used to optimize
   * images
   */
  maxContainerWidth?: number
  /**
   * The color scheme for text included in the tile
   */
  colorScheme?: "light" | "gray" | "dark"
  /**
   * The theme applied to the page the tile is displayed on. This is used to
   * style text within the tile
   */
  theme?: "base" | "article" | "compact" | "landingPage"
  /**
   * The text displayed above the headline
   */
  eyebrow?: string
  /**
   * The headline displayed in the tile
   */
  headline?: string
  /**
   * The content item to display as the media in the tile
   */
  media: any
  /**
   * The position of the media in the tile
   */
  mediaPosition?: "left" | "right"
  /**
   * The ratio to crop and display media in the tile. Used to set the minimum
   * height of the when the text column is shorter than the media column
   */
  mediaRatio?: 0.666 | 1 | 1.5
  /**
   * The width of the media column in the tile
   */
  mediaWidth?: "n40" | "n50"
  /**
   * Whether the media column should stretch to fill the height of the tile
   */
  stretchMedia?: boolean
  /**
   * The text displayed in the tile
   */
  text?: string
  /**
   * The content items to display as modular content within text
   */
  textLinkedItems?: any[]
  /**
   * The content items used to resolve links within text
   */
  textLinks?: any[]
  /**
   * Label for the link displayed at the bottom of the tile
   */
  linkLabel?: string
  /**
   * The content item to link to from the link displayed at the bottom of the
   * tile
   */
  linkTo?: any
}

/**
 * Displays text and an image in columns. It can be used as a section of
 * content where the image grows to fill the height of the text column or styled
 * as a card to use as a prominent call to action
 */
const MediaTile = ({
  colorScheme,
  style,
  maxContainerWidth = 750,
  theme,
  eyebrow,
  headline,
  media,
  mediaPosition = "left",
  mediaRatio = 1,
  mediaWidth = "n50",
  stretchMedia = true,
  text,
  textLinkedItems,
  textLinks,
  linkLabel,
  linkTo,
}: MediaTileProps) => {
  const textColumnClassName = classNames(
    "w-full self-center",
    stretchMedia === true ? `sm:py-8` : ``,
    mediaWidth === "n40" ? `sm:w-3/5` : `sm:w-1/2`,
    style !== "card" ? [mediaPosition === "left" ? `sm:pl-6` : `sm:pr-6`] : ``
  )

  const mediaColumnClassName = classNames(
    "w-full mt-8 sm:mt-0",
    stretchMedia ? `self-stretch` : ``,
    mediaWidth === "n40" ? `sm:w-2/5` : `sm:w-1/2`,
    mediaPosition === "left" ? `order-last sm:order-none` : `order-last`,
    style !== "card" ? [mediaPosition === "left" ? `sm:pr-6` : `sm:pl-6`] : ``
  )
  // Widths are used to calculate the size images in these regions should be cropped to
  const mediaCropWidth = {
    n40: maxContainerWidth * 0.4,
    n50: maxContainerWidth * 0.5,
  }[mediaWidth || "n50"]

  return (
    <div
      className={
        style === `card`
          ? `block rounded-lg shadow-md border border-gray-300 overflow-hidden`
          : ``
      }
      data-analytics-region="Media Tile"
    >
      <div className={`flex flex-wrap`}>
        {media && (
          <div className={mediaColumnClassName} position={mediaPosition}>
            <div className={`relative ${stretchMedia ? `h-full` : ``}`}>
              <Media
                contentItem={media}
                mediaRatio={mediaRatio}
                cropWidth={mediaCropWidth}
              />
            </div>
          </div>
        )}
        <div className={textColumnClassName}>
          <BodyStyles
            className={style === `card` ? `p-6` : ``}
            colorScheme={colorScheme}
            theme={theme}
          >
            {headline && (
              <Heading size="2xl" text={headline} eyebrow={eyebrow} />
            )}
            <RichText
              colorScheme={colorScheme}
              content={text}
              linkedItems={textLinkedItems}
              links={textLinks}
              theme={theme}
            />
            {linkTo && linkLabel ? (
              <p>
                <LinkElement
                  contentItem={{
                    elements: {
                      label: {
                        value: linkLabel,
                      },
                      link_to: {
                        value: [linkTo],
                      },
                      style: {
                        value: [
                          {
                            codename: "more",
                          },
                        ],
                      },
                    },
                  }}
                />
              </p>
            ) : null}
          </BodyStyles>
        </div>
      </div>
    </div>
  )
}

export default MediaTile
